import { EMBEDDED_DASHBOARD_PATHS } from "components/EmbeddedAnalytics/types";

import { useRoleContext } from "./useRoleContext";
import { useSwrFetch } from "./useSwrFetch";

import type {
  EmbeddedDashboardClientInit,
  EmbeddedDashboardName,
} from "components/EmbeddedAnalytics/types";

interface UseEmbeddedDashboardSessionProps {
  dashboard: EmbeddedDashboardName;
}

/**
 * Fetches a viewer session for an embedded dashboard.
 *
 * The session is minted server-side and carries the viewer's data scope, so
 * nothing here (or in the browser) can widen what the dashboard returns.
 * `refetch` is what the renderer calls when the session expires.
 */
export function useEmbeddedDashboardSession(
  props: UseEmbeddedDashboardSessionProps,
) {
  const { dashboard } = props;
  const { role } = useRoleContext();

  const url =
    role?.type === "SHIPPER"
      ? `/shippers/${role.id}/viz/embedded/${EMBEDDED_DASHBOARD_PATHS[dashboard]}`
      : null;

  const { data, loading, error, refetch } =
    useSwrFetch<EmbeddedDashboardClientInit>({
      url,
      errorMessage:
        "Failed to load the dashboard. Please contact us if the issue persists.",
      // Sessions expire (24h). The embed component exposes no expiry event, so
      // refresh when the user returns to the tab — a long-lived tab would
      // otherwise sit on a dead session.
      swrOptions: { revalidateOnFocus: true },
    });

  return { clientInit: data ?? null, loading, error, refetch };
}
